import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IMomentDenormalized,
  SubTaskQuestionAnswerAddress,
  SubTaskQuestionAnswerLinearScale,
  SubTaskQuestionAnswerMultiSelect,
  SubTaskQuestionAnswerRating,
  SubTaskQuestionAnswerSelect,
  SubTaskQuestionAnswerSupportingActor,
  SubTaskQuestionAnswerText,
  SubTaskType,
  SwagUpChoiceAnswerData
} from '@dadohr/shared/domainTypes/experience';
import { ISubmitFileMonitor } from '@dadohr/shared/domainTypes/submitFileMonitor';
import styled from '@emotion/styled';
import { Button, IconButton, Typography } from '@material-ui/core';
import { compact, truncate } from 'lodash';
import { CircleArrowLeft, CircleArrowRight } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical,
  FlexItem
} from '../../../../../../../layout/Flex';
import {
  SubTaskContext,
  TaskBody
} from '../../../../../../features/Experience/components/Task';
import { useForceRerender } from '../../../../../../features/Experience/services/forceRerender';
import {
  answerMomentSubTask,
  answerMomentSubTasks,
  completeMomentSubTask,
  getMomentSubTaskVisitUrl,
  markMomentAsRead,
  trackMomentSubTaskCompletion
} from '../../../../../../features/Moment/service';
import { ROUTES_V2 } from '../../../../../../routes';
import { WithBreakpoints } from '../../../components/Breakpoints';
import { EmployeeAppCard } from '../../../components/EmployeeAppCard';
import { PlainDate } from '../../../components/Tasks';

const Container = styled('div')((p) => ({
  width: '100%'
}));

// const getTaskStatusFromMomentStatus = (
//   status: MomentInstanceStatus
// ): TaskStatus => {
//   switch (status) {
//     case MomentInstanceStatus.OPEN:
//       return TaskStatus.RUNNING;
//     case MomentInstanceStatus.PENDING:
//       return TaskStatus.NOT_STARTED;
//     case MomentInstanceStatus.COMPLETED:
//       return TaskStatus.COMPLETE;
//     case MomentInstanceStatus.ERROR:
//       return TaskStatus.COMPLETE;
//   }
// };

const MarkAsReadButton = ({
  momentInstanceAccessToken,
  instance
}: {
  momentInstanceAccessToken: string;
  instance: Doc<IMomentDenormalized>;
}) => {
  const { t } = useUnsafeTranslation();
  const { forceRerender } = useForceRerender();
  const markedAsRead = !!instance.data.markedAsRead;
  const onClick = async (nextValue: boolean) => {
    await markMomentAsRead(
      momentInstanceAccessToken,
      instance.data.instanceId,
      nextValue
    );
    forceRerender();
  };
  if (markedAsRead) {
    return (
      <ButtonWithPromise
        color="primary"
        variant="outlined"
        pending={t('Unarchiving...')}
        size="small"
        onClick={() => onClick(false)}
      >
        {t('Unarchive')}
      </ButtonWithPromise>
    );
  }
  return (
    <ButtonWithPromise
      color="primary"
      variant="contained"
      pending={t('Archiving...')}
      size="small"
      onClick={() => onClick(true)}
    >
      {t('Archive')}
    </ButtonWithPromise>
  );
};

export const InboxMomentDetails = ({
  instance,
  actorAccessToken,
  momentInstanceAccessToken,
  returnTo,
  noBackButton = false,
  additionalLinkInHeader = null,
  nextButtons = EMPTY_ARR
}: {
  instance: Doc<IMomentDenormalized>;
  actorAccessToken: string;
  momentInstanceAccessToken: string;
  returnTo?: string;
  noBackButton?: boolean;
  nextButtons?: {
    label: string;
    url: string;
    variant?: 'outlined' | 'contained';
  }[];
  additionalLinkInHeader?: {
    label: string;
    url: string;
  } | null;
}) => {
  const backUrl =
    returnTo || ROUTES_V2.inbox.url(actorAccessToken, { g: 'moments-active' });

  const title = (
    <FlexItem flex={3}>
      <FlexContainerVertical spacing={0.5}>
        <Typography variant="h4">{instance.data.subject}</Typography>
        <FlexContainer spacing={3} wrap="wrap" rowSpacing={0.5}>
          <Typography variant="h5" color="textSecondary">
            {compact([instance.data.from.name, instance.data.from.email]).join(
              ' • '
            )}
          </Typography>
          {additionalLinkInHeader && (
            <Link to={additionalLinkInHeader.url}>
              <Typography variant="body2" color="primary">
                {additionalLinkInHeader.label} <CircleArrowRight size={12} />
              </Typography>
            </Link>
          )}
        </FlexContainer>
      </FlexContainerVertical>
    </FlexItem>
  );

  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The parent element is the scroll container. When we move between moemnts, go back to the top
    ref.current?.parentElement?.scrollTo(0, 0);
  }, [momentInstanceAccessToken]);

  const subTaskContext: SubTaskContext = useMemo(
    () => ({
      tz: instance.data.org.tz,
      user: instance.data.user,
      userId: instance.data.userId,
      answerSubTask: function (
        subTaskId: string,
        value:
          | SubTaskQuestionAnswerText
          | SubTaskQuestionAnswerAddress
          | SubTaskQuestionAnswerSelect
          | SubTaskQuestionAnswerMultiSelect
          | SwagUpChoiceAnswerData
          | SubTaskQuestionAnswerSupportingActor
          | SubTaskQuestionAnswerLinearScale
          | SubTaskQuestionAnswerRating
      ): Promise<any> {
        return answerMomentSubTask(instance.data.instanceId, {
          subTaskId,
          d: value
        });
      },
      answerSubTasks: function (
        answers: {
          subTaskId: string;
          d:
            | SubTaskQuestionAnswerText
            | SubTaskQuestionAnswerAddress
            | SubTaskQuestionAnswerSelect
            | SubTaskQuestionAnswerMultiSelect
            | SwagUpChoiceAnswerData
            | SubTaskQuestionAnswerSupportingActor
            | SubTaskQuestionAnswerLinearScale
            | SubTaskQuestionAnswerRating;
        }[]
      ): Promise<any> {
        return answerMomentSubTasks(instance.data.instanceId, answers);
      },
      completeSubTask: (subTaskId: string) => {
        return completeMomentSubTask(
          instance.data.instanceId,
          subTaskId
        ).then(() => ({}));
      },

      getSubTaskVisitUrl: function (subtaskId: string, url: string): string {
        return getMomentSubTaskVisitUrl(
          url,
          instance.data.instanceId,
          subtaskId
        );
      },
      submitFileMonitorId: (subTaskId: string) => {
        throw new Error('Function not Implemented');
      },
      constructNextFileSubmitMonitor: function (
        file: File,
        subTaskId: string
      ): ISubmitFileMonitor {
        throw new Error('Function not Implemented');
      },
      trackSubTaskCompletion: (subTaskId: string, subTaskType: SubTaskType) => {
        return trackMomentSubTaskCompletion(
          instance.data.instanceId,
          subTaskId,
          subTaskType
        );
      },
      answerComplexSubtask: function (
        subtaskId: string,
        value:
          | SubTaskQuestionAnswerText
          | SubTaskQuestionAnswerAddress
          | SubTaskQuestionAnswerSelect
          | SubTaskQuestionAnswerMultiSelect
          | SwagUpChoiceAnswerData
          | SubTaskQuestionAnswerSupportingActor
          | SubTaskQuestionAnswerLinearScale
          | SubTaskQuestionAnswerRating
      ): Promise<any> {
        throw new Error('Function not implemented.');
      },
      trackEvent: () => null
    }),
    [instance]
  );

  return (
    <Container ref={ref}>
      <FlexContainerVertical fullWidth spacing={3}>
        <FlexContainerVertical fullWidth spacing={2}>
          {!noBackButton && (
            <Link to={backUrl}>
              <IconButton color="primary">
                <CircleArrowLeft size={24} />
              </IconButton>
            </Link>
          )}
          <WithBreakpoints
            lg={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    {!instance.data.archivedBasedOnTime && (
                      <MarkAsReadButton
                        momentInstanceAccessToken={momentInstanceAccessToken}
                        instance={instance}
                      />
                    )}
                    <PlainDate ts={instance.data.sendAt} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            md={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    <MarkAsReadButton
                      momentInstanceAccessToken={momentInstanceAccessToken}
                      instance={instance}
                    />
                    <PlainDate ts={instance.data.sendAt} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            sm={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    <MarkAsReadButton
                      momentInstanceAccessToken={momentInstanceAccessToken}
                      instance={instance}
                    />
                    <PlainDate ts={instance.data.sendAt} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            xs={
              <FlexContainerVertical fullWidth spacing={1}>
                {title}
                <FlexContainer>
                  <MarkAsReadButton
                    momentInstanceAccessToken={momentInstanceAccessToken}
                    instance={instance}
                  />
                  <PlainDate ts={instance.data.sendAt} />
                </FlexContainer>
              </FlexContainerVertical>
            }
          />
          <EmployeeAppCard>
            <TaskBody
              taskId={''}
              subtasks={instance.data.content.blocks}
              context={{
                user: instance.data.user,
                userId: instance.data.userId,
                org: instance.data.org,
                subTaskContext: subTaskContext
              }}
              noContainerMargins
            />
          </EmployeeAppCard>
        </FlexContainerVertical>
        {nextButtons.length > 0 && (
          <FlexContainer>
            {nextButtons.map((nextButton, i) => (
              <Link to={nextButton.url} key={i}>
                <Button color="primary" variant={'outlined'}>
                  {truncate(nextButton.label, {
                    length: Math.round(100 / nextButtons.length)
                  })}
                </Button>
              </Link>
            ))}
          </FlexContainer>
        )}
      </FlexContainerVertical>
    </Container>
  );
};
